import axios from 'axios';
import { toast } from 'react-hot-toast';


// Create axios instance with default config
// Normalize base URL to ensure it ends with /api
const rawBase = import.meta.env.VITE_APP_API_URL;
const baseURL = /\/api\/?$/.test(rawBase) ? rawBase.replace(/\/+$/, '/api') : `${rawBase.replace(/\/+$/, '')}/api`;

const api = axios.create({
    baseURL,
    withCredentials: true,
    timeout: 30000, // 30 second timeout
    // headers: {
    //     'Content-Type': 'application/json',
    //     'Accept': 'application/json'
    // }
});

const generateRequestId = () => {
    try {
        if (typeof globalThis !== 'undefined') {
            const cryptoObj = globalThis.crypto;
            if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
                return cryptoObj.randomUUID();
            }
            if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
                const bytes = new Uint8Array(16);
                cryptoObj.getRandomValues(bytes);
                return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
            }
        }
    } catch (e) {
        // Ignore and fallback
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
};

// Request interceptor
api.interceptors.request.use(
    async config => {
        config.headers = config.headers || {};
        // Get CSRF token from cookie
        const csrfToken = document.cookie
            .split('; ')
            .find(row => row.startsWith('csrftoken='))
            ?.split('=')[1];

        if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken;
        }

        // Add request ID for tracking
        config.headers['X-Request-ID'] = generateRequestId();

        return config;
    },
    error => {
        console.error('Request error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    response => {
        // Normalize media URLs to include correct host:port (especially in production with custom port)
        try {
            const apiUrl = import.meta.env.VITE_APP_API_URL;
            const apiHost = new URL(apiUrl).host; // includes port if present

            const fixMediaUrl = (url) => {
                if (typeof url === 'string' && url.includes('/media/')) {
                    // Replace host portion while preserving protocol
                    return url.replace(/(\/\/[^/]+)(\/media\/)/, `//${apiHost}$2`);
                }
                return url;
            };

            const fixObject = (obj) => {
                if (!obj || typeof obj !== 'object') return obj;
                if (Array.isArray(obj)) return obj.map(fixObject);
                const fixed = {};
                for (const key in obj) {
                    if (key === 'avatar' || key === 'avatar_url' || key === 'cover_image' || key === 'cover_url' || key === 'coverImage' || key === 'image') {
                        fixed[key] = fixMediaUrl(obj[key]);
                    } else if (typeof obj[key] === 'object') {
                        fixed[key] = fixObject(obj[key]);
                    } else {
                        fixed[key] = obj[key];
                    }
                }
                return fixed;
            };

            if (response.data) {
                response.data = fixObject(response.data);
            }
        } catch (e) {
            // Silent fail; do not block response if URL parsing fails
            console.warn('Media URL normalization skipped:', e);
        }
        return response;
    },
    error => {
        // Handle different types of errors
        if (error.response) {
            // Check if response is HTML (nginx error page)
            const contentType = error.response.headers['content-type'] || '';
            const isHtml = contentType.includes('text/html');
            
            if (isHtml) {
                // Handle HTML error responses (nginx errors)
                switch (error.response.status) {
                    case 500:
                        toast.error('Server error occurred. Please try again later.');
                        break;
                    case 502:
                        toast.error('Service temporarily unavailable. Please try again later.');
                        break;
                    case 503:
                        toast.error('Service unavailable. Please try again later.');
                        break;
                    case 504:
                        toast.error('Request timeout. Please try again.');
                        break;
                    default:
                        toast.error('Server error occurred. Please try again later.');
                }
            } else {
                // Handle JSON error responses
                switch (error.response.status) {
                    case 401:
                        toast.error('Authentication required. Please log in.');
                        break;
                    case 403:
                        toast.error('You do not have permission to perform this action.');
                        break;
                    case 404:
                        toast.error('The requested resource was not found.');
                        break;
                    case 429:
                        toast.error('Rate limit exceeded. Please try again later.');
                        break;
                    case 500:
                        toast.error('An internal server error occurred. Please try again later.');
                        break;
                    default:
                        toast.error('An error occurred. Please try again.');
                }
            }
        } else if (error.request) {
            // Network error
            toast.error('Unable to connect to the server. Please check your internet connection.');
        } else {
            toast.error('An unexpected error occurred.');
        }

        // Log error for debugging
        console.error('API Error:', {
            config: error.config,
            status: error.response?.status,
            data: error.response?.data,
            headers: error.response?.headers
        });

        return Promise.reject(error);
    }
);

// API methods
export const secureApi = {
    get: async (url, config = {}) => {
        // eslint-disable-next-line no-useless-catch
        try {
            const response = await api.get(url, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    post: async (url, data = {}, config = {}) => {
        // eslint-disable-next-line no-useless-catch
        try {
            const response = await api.post(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    patch: async (url, data = {}, config = {}) => {
        try {
            const response = await api.patch(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    put: async (url, data = {}, config = {}) => {
        // eslint-disable-next-line no-useless-catch
        try {
            const response = await api.put(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    delete: async (url, config = {}) => {
        // eslint-disable-next-line no-useless-catch
        try {
            const response = await api.delete(url, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
};

export default secureApi;
